import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { Upload, Globe, CreditCard, MapPin, Shield, Calendar as CalendarIcon, Sparkles, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

interface ArcadeSpotBookingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  position: number | null;
  onSuccess: () => void;
}

export const ArcadeSpotBookingDialog: React.FC<ArcadeSpotBookingDialogProps> = ({
  isOpen,
  onClose,
  position,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isAdmin } = useAdminCheck();
  const { toast } = useToast();
  
  const [selectedPosition, setSelectedPosition] = useState<number | null>(position);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [dailyPrice, setDailyPrice] = useState(10);
  const [existingBookings, setExistingBookings] = useState<any[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (isOpen && user) {
      fetchDailyPrice();
    }
  }, [isOpen, user]);

  useEffect(() => {
    setSelectedPosition(position);
  }, [position]);

  useEffect(() => {
    if (selectedPosition) {
      fetchExistingBookings(selectedPosition);
    }
  }, [selectedPosition]);

  useEffect(() => {
    if (selectedDates.length > 0) {
      calculatePrice();
    } else {
      setTotalPrice(0);
    }
  }, [selectedDates, dailyPrice]);

  const fetchDailyPrice = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'arcade_spot_daily_price_cents')
        .maybeSingle();

      if (error) throw error;
      
      const priceInCents = data?.setting_value ? parseInt(String(data.setting_value)) : 1000;
      setDailyPrice(priceInCents / 100);
    } catch (error) {
      console.error('Error fetching daily price:', error);
    }
  };

  const fetchExistingBookings = async (positionNumber: number) => {
    try {
      const { data, error } = await supabase
        .from('arcade_spots')
        .select('start_date, end_date')
        .eq('position', positionNumber)
        .in('status', ['paid', 'active']);

      if (error) throw error;
      setExistingBookings(data || []);
    } catch (error) {
      console.error('Error fetching existing bookings:', error);
    }
  };

  const isDateDisabled = (date: Date) => {
    // Permettre le jour même - ne bloquer que les dates passées (avant aujourd'hui)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    
    return existingBookings.some(booking => {
      return dateStr >= booking.start_date && dateStr <= booking.end_date;
    });
  };

  const calculatePrice = async () => {
    if (selectedDates.length === 0) {
      setTotalPrice(0);
      return;
    }

    try {
      // Nombre de jours sélectionnés
      const days = selectedDates.length;
      
      const { data, error } = await supabase
        .rpc('calculate_arcade_spot_price', { p_days: days });

      if (error) throw error;
      setTotalPrice(data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `arcade-spot-${Date.now()}.${fileExt}`;
    const filePath = `arcade-spots/${fileName}`;

    const { error } = await supabase.storage
      .from('server-images')
      .upload(filePath, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from('server-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Type de fichier invalide",
        description: "Veuillez sélectionner une image (JPEG, PNG, GIF, WebP)",
        variant: "destructive",
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "Fichier trop volumineux",
        description: "L'image ne doit pas dépasser 5MB",
        variant: "destructive",
      });
      return;
    }

    setImageFile(file);
    setImageUrl(''); // Clear URL input if file is selected
    
    // Créer aperçu
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setImageUrl(url);
    
    if (url) {
      setImageFile(null); // Clear file input if URL is entered
      setImagePreview(url);
    } else {
      setImagePreview('');
    }
  };

  const selectEntireMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const allDays = eachDayOfInterval({ start, end });
    
    // Filtrer les jours disponibles
    const availableDays = allDays.filter(day => !isDateDisabled(day));
    
    // Fusionner avec les dates déjà sélectionnées
    const uniqueDates = Array.from(
      new Map(
        [...selectedDates, ...availableDays].map(d => [format(d, 'yyyy-MM-dd'), d])
      ).values()
    ).sort((a, b) => a.getTime() - b.getTime());
    
    setSelectedDates(uniqueDates);
    
    toast({
      title: t('arcade.selectMonth'),
      description: `${uniqueDates.length} ${t('arcade.daysSelected')}`,
    });
  };

  const clearSelection = () => {
    setSelectedDates([]);
  };

  const handleSubmit = async () => {
    if (!selectedPosition) {
      toast({
        title: "Position manquante",
        description: "Veuillez sélectionner une position d'Arcade Spot",
        variant: "destructive",
      });
      return;
    }

    if (selectedDates.length === 0) {
      toast({
        title: "Dates manquantes",
        description: "Veuillez sélectionner au moins un jour",
        variant: "destructive",
      });
      return;
    }

    if (!imageFile && !imageUrl) {
      toast({
        title: "Image manquante",
        description: "Veuillez uploader une image ou saisir une URL d'image",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Upload de l'image ou utiliser l'URL
      const finalImageUrl = imageFile ? await uploadImage(imageFile) : imageUrl;

      // Convertir les dates sélectionnées en plage continue
      const sortedDates = selectedDates.sort((a, b) => a.getTime() - b.getTime());
      const startDate = sortedDates[0];
      const endDate = sortedDates[sortedDates.length - 1];

      // Créer le paiement
      const { data, error } = await supabase.functions.invoke('create-arcade-spot-payment', {
        body: {
          position: selectedPosition,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          imageUrl: finalImageUrl,
          websiteUrl: websiteUrl || null,
        },
      });

      if (error) throw error;

      if (data.url) {
        // Rediriger vers Stripe
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirection vers le paiement",
          description: "Vous allez être redirigé vers Stripe pour finaliser votre réservation",
        });

        onSuccess();
      }
    } catch (error: any) {
      console.error('Error creating arcade spot:', error);
      toast({
        title: "Erreur lors de la réservation",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminAdd = async () => {
    if (!selectedPosition) {
      toast({
        title: "Position manquante",
        description: "Veuillez sélectionner une position d'Arcade Spot",
        variant: "destructive",
      });
      return;
    }

    if (selectedDates.length === 0) {
      toast({
        title: "Dates manquantes", 
        description: "Veuillez sélectionner au moins un jour",
        variant: "destructive",
      });
      return;
    }

    if (!imageFile && !imageUrl) {
      toast({
        title: "Image manquante",
        description: "Veuillez uploader une image ou saisir une URL d'image",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Upload de l'image ou utiliser l'URL
      const finalImageUrl = imageFile ? await uploadImage(imageFile) : imageUrl;

      // Convertir les dates sélectionnées en plage continue
      const sortedDates = selectedDates.sort((a, b) => a.getTime() - b.getTime());
      const startDate = sortedDates[0];
      const endDate = sortedDates[sortedDates.length - 1];

      // Créer directement l'arcade spot (admin)
      const { error } = await supabase
        .from('arcade_spots')
        .insert({
          server_id: null,
          position: selectedPosition,
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          image_url: finalImageUrl,
          website_url: websiteUrl || null,
          price_per_day: 0, // Gratuit pour admin
          total_amount: 0,
          status: 'active',
        });

      if (error) throw error;

      toast({
        title: "Arcade Spot créé",
        description: "L'Arcade Spot a été ajouté avec succès",
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating admin arcade spot:', error);
      toast({
        title: "Erreur lors de la création",
        description: error.message || "Une erreur est survenue",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedDates([]);
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
    setWebsiteUrl('');
    setTotalPrice(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!user) {
    return null;
  }

  const positionNames = {
    1: t('arcade.booking.positionLeft'),
    2: t('arcade.booking.positionCenter'), 
    3: t('arcade.booking.positionRight')
  };

  //console.log('ArcadeSpotBookingDialog: Rendering with isOpen:', isOpen, 'user:', !!user);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gaming-primary/20 rounded-lg flex items-center justify-center">
              🕹️
            </div>
            {t('arcade.booking.title')}
            {selectedPosition && ` - ${positionNames[selectedPosition as keyof typeof positionNames]}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Sélection de position */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {t('arcade.booking.positionLabel')}
            </Label>
            <Select 
              value={selectedPosition?.toString() || ""} 
              onValueChange={(value) => setSelectedPosition(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue>
                  {selectedPosition 
                    ? `${selectedPosition === 1 ? '🎮' : selectedPosition === 2 ? '🎯' : '🕹️'} ${positionNames[selectedPosition as keyof typeof positionNames]}`
                    : t('arcade.booking.selectPosition')
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">🎮 {t('arcade.booking.positionLeft')}</SelectItem>
                <SelectItem value="2">🎯 {t('arcade.booking.positionCenter')}</SelectItem>
                <SelectItem value="3">🕹️ {t('arcade.booking.positionRight')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Calendrier de sélection multiple */}
          {selectedPosition && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                {t('arcade.booking.selectDates')}
              </Label>
              
              {/* Boutons rapides */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={selectEntireMonth}
                  className="text-xs"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('arcade.selectMonth')}
                </Button>
                {selectedDates.length > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={clearSelection}
                    className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <X className="w-3 h-3 mr-1" />
                    {t('arcade.clearSelection')}
                  </Button>
                )}
              </div>

              <div className="border border-slate-700 rounded-lg p-4 bg-slate-800">
                <Calendar
                  mode="multiple"
                  selected={selectedDates}
                  onSelect={(dates) => setSelectedDates(dates || [])}
                  disabled={isDateDisabled}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  className="pointer-events-auto mx-auto"
                  classNames={{
                    day_selected: "bg-gaming-primary text-white font-bold hover:bg-gaming-primary/90 ring-2 ring-gaming-primary ring-offset-2 ring-offset-slate-800",
                    day: "text-slate-300 hover:bg-slate-700 rounded-md cursor-pointer",
                    day_today: "bg-slate-700 text-white font-semibold",
                  }}
                />
              {selectedDates.length > 0 && (
                <div className="mt-4 p-3 bg-gaming-primary/10 border border-gaming-primary/30 rounded-lg">
                  <p className="text-sm text-gaming-primary font-semibold mb-2">
                    {t('arcade.booking.daysSelected')} ({selectedDates.length}) :
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedDates
                      .sort((a, b) => a.getTime() - b.getTime())
                      .map((date, index) => (
                        <span 
                          key={index}
                          className="text-xs bg-gaming-primary text-white font-medium px-2 py-1 rounded shadow-sm"
                        >
                          {format(date, 'dd/MM', { locale: fr })}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
            </div>
          )}

          {/* Image */}
          <div className="space-y-2">
            <Label>{t('arcade.booking.imageLabel')} {t('arcade.booking.imageFormat')}</Label>
            <div className="flex flex-col gap-3">
              <div>
                <Label htmlFor="image" className="text-sm text-slate-400">{t('arcade.booking.option1')} {t('arcade.booking.maxSize')}</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="cursor-pointer mt-1"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <div className="h-px bg-slate-600 flex-1"></div>
                <span className="text-xs text-slate-500">{t('arcade.booking.or')}</span>
                <div className="h-px bg-slate-600 flex-1"></div>
              </div>
              
              <div>
                <Label htmlFor="imageUrl" className="text-sm text-slate-400">{t('arcade.booking.option2')}</Label>
                <Input
                  id="imageUrl"
                  type="url"
                  value={imageUrl}
                  onChange={handleImageUrlChange}
                  placeholder={t('arcade.booking.urlPlaceholder')}
                  className="mt-1"
                />
              </div>
              
              {imagePreview && (
                <div className="w-64 aspect-[16/9] border border-slate-700 rounded-md overflow-hidden mx-auto">
                   <img 
                    src={imagePreview} 
                    alt={t('arcade.booking.preview')} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>

          {/* URL du site web */}
          <div className="space-y-2">
            <Label htmlFor="website">{t('arcade.booking.websiteLabel')}</Label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                id="website"
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder={t('arcade.booking.websitePlaceholder')}
                className="pl-10"
              />
            </div>
          </div>

          {/* Prix */}
          {totalPrice > 0 && (
              <div className="bg-slate-800/50 p-4 rounded-lg border border-gaming-primary/20">
              <div className="flex justify-between items-center">
                <span className="text-slate-300">{t('arcade.booking.totalPrice')} :</span>
                <span className="text-2xl font-bold text-gaming-primary">
                  {(totalPrice / 100).toFixed(2)} €
                </span>
              </div>
              <p className="text-sm text-slate-400 mt-1">
                {selectedDates.length} {t('arcade.booking.days')} × {dailyPrice}€
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              {t('button.cancel')}
            </Button>
            
            {/* Bouton Admin uniquement pour les admins */}
            {isAdmin && (
              <Button 
                onClick={handleAdminAdd} 
                disabled={isLoading || !selectedPosition || selectedDates.length === 0 || (!imageFile && !imageUrl)}
                variant="secondary"
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
              >
              {isLoading ? (
                t('arcade.booking.processing')
              ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    {t('arcade.booking.addAdmin')}
                  </>
                )}
              </Button>
            )}
            
            <Button 
              onClick={handleSubmit} 
              disabled={isLoading || !selectedPosition || selectedDates.length === 0 || (!imageFile && !imageUrl)}
              className="flex-1"
            >
              {isLoading ? (
                t('arcade.booking.processing')
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  {t('arcade.booking.proceedPayment')}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};